'use client'

import { useEffect } from 'react'
import { AUTH_LOGOUT_COOLDOWN_UNTIL_KEY } from '@/lib/auth/logout-state'

const PRELOAD_INITIAL_DELAY_MS = 5000
const PRELOAD_BETWEEN_TASKS_MS = 2500
const PRELOAD_IDLE_TIMEOUT_MS = 2500

const HOME_WORKSPACE_PRELOAD_TASKS = [
  () => import('@/components/Navbar'),
  () => import('@/app/[locale]/workspace/page'),
]

function isLogoutCoolingDown() {
  const cooldownUntil = Number(window.sessionStorage.getItem(AUTH_LOGOUT_COOLDOWN_UNTIL_KEY) ?? 0)
  return Number.isFinite(cooldownUntil) && Date.now() < cooldownUntil
}

function shouldSkipWorkspacePreload() {
  const connection = (window.navigator as Navigator & {
    connection?: {
      saveData?: boolean
      effectiveType?: string
    }
  }).connection
  if (!connection) return false
  if (connection.saveData) return true
  return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g'
}

export function HomeWorkspacePreloader({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    if (shouldSkipWorkspacePreload()) return

    let stopped = false
    const timeoutIds: number[] = []
    const idleIds: number[] = []
    const visibilityListeners: Array<() => void> = []

    const scheduleTimeout = (callback: () => void, delay: number) => {
      const timeoutId = window.setTimeout(callback, delay)
      timeoutIds.push(timeoutId)
    }

    const scheduleIdle = (callback: () => void) => {
      if ('requestIdleCallback' in window) {
        const idleId = window.requestIdleCallback(callback, { timeout: PRELOAD_IDLE_TIMEOUT_MS })
        idleIds.push(idleId)
        return
      }

      scheduleTimeout(callback, 0)
    }

    const runWhenVisible = (callback: () => void) => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') return
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        callback()
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
      visibilityListeners.push(() => document.removeEventListener('visibilitychange', handleVisibilityChange))
    }

    const runNext = (index: number) => {
      if (stopped || index >= HOME_WORKSPACE_PRELOAD_TASKS.length) return
      if (document.visibilityState === 'hidden') {
        runWhenVisible(() => runNext(index))
        return
      }
      if (isLogoutCoolingDown()) return

      scheduleIdle(() => {
        if (stopped) return

        void HOME_WORKSPACE_PRELOAD_TASKS[index]()
          .catch(() => undefined)
          .finally(() => {
            if (!stopped) {
              scheduleTimeout(() => runNext(index + 1), PRELOAD_BETWEEN_TASKS_MS)
            }
          })
      })
    }

    scheduleTimeout(() => runNext(0), PRELOAD_INITIAL_DELAY_MS)

    return () => {
      stopped = true
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))
      visibilityListeners.forEach((removeListener) => removeListener())
      if ('cancelIdleCallback' in window) {
        idleIds.forEach((idleId) => window.cancelIdleCallback(idleId))
      }
    }
  }, [enabled])

  return null
}
